import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { NetworkState } from 'src/constants/network-state';
import { useAppSelector } from 'src/hooks/hooks';
import { useApplicationGroups } from 'src/hooks/useApplicationGroups';
import { useApplications } from 'src/hooks/useApplications';
import { getAppContextFromPath } from 'src/router/pathUtils';
import pages from 'src/router/routes';
import { isAllSelection } from 'src/utils/selection';

export const useUrlContextValidation = (isUrlNavigationEnabled: boolean) => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.user);
  const lastRejectedScopedPathRef = useRef<string | null>(null);
  const urlContext = getAppContextFromPath(location.pathname);
  const isAuthReadyForScopeQueries =
    user.status === NetworkState.SUCCESS && Boolean(user.user?.uid);
  const shouldValidateUrlContext =
    isUrlNavigationEnabled &&
    isAuthReadyForScopeQueries &&
    urlContext.groupId !== undefined;
  const shouldValidateApplications =
    shouldValidateUrlContext &&
    urlContext.appId !== undefined &&
    !isAllSelection(urlContext.groupId) &&
    !isAllSelection(urlContext.appId);
  const {
    applicationGroups: urlApplicationGroups,
    isLoading: areUrlApplicationGroupsLoading,
    isFetching: areUrlApplicationGroupsFetching,
    isSuccess: areUrlApplicationGroupsSuccess,
    isError: areUrlApplicationGroupsError,
  } = useApplicationGroups({
    skip: !shouldValidateUrlContext,
    syncSelection: false,
  });
  const {
    applications: urlApplications,
    isLoading: areUrlApplicationsLoading,
    isFetching: areUrlApplicationsFetching,
    isSuccess: areUrlApplicationsSuccess,
    isError: areUrlApplicationsError,
  } = useApplications({
    skip: !shouldValidateApplications,
    syncSelection: false,
  });

  useEffect(() => {
    if (!shouldValidateUrlContext) {
      lastRejectedScopedPathRef.current = null;
      return;
    }

    const rejectScopedPath = () => {
      if (lastRejectedScopedPathRef.current === location.pathname) {
        return;
      }

      lastRejectedScopedPathRef.current = location.pathname;
      toast.error('You do not have access to this application context.');
      navigate(pages.applicationGroup.path, { replace: true });
    };

    if (areUrlApplicationGroupsError) {
      rejectScopedPath();
      return;
    }

    if (
      areUrlApplicationGroupsLoading ||
      areUrlApplicationGroupsFetching ||
      !areUrlApplicationGroupsSuccess
    ) {
      return;
    }

    if (
      urlContext.groupId &&
      !isAllSelection(urlContext.groupId) &&
      !urlApplicationGroups.some((group) => group._id === urlContext.groupId)
    ) {
      rejectScopedPath();
      return;
    }

    if (
      urlContext.groupId &&
      isAllSelection(urlContext.groupId) &&
      urlContext.appId &&
      !isAllSelection(urlContext.appId)
    ) {
      rejectScopedPath();
      return;
    }

    if (!shouldValidateApplications) {
      lastRejectedScopedPathRef.current = null;
      return;
    }

    if (areUrlApplicationsError) {
      rejectScopedPath();
      return;
    }

    if (
      areUrlApplicationsLoading ||
      areUrlApplicationsFetching ||
      !areUrlApplicationsSuccess
    ) {
      return;
    }

    if (
      urlContext.appId &&
      !isAllSelection(urlContext.appId) &&
      !urlApplications.some(
        (application) => application._id === urlContext.appId
      )
    ) {
      rejectScopedPath();
      return;
    }

    lastRejectedScopedPathRef.current = null;
  }, [
    areUrlApplicationGroupsError,
    areUrlApplicationGroupsFetching,
    areUrlApplicationGroupsLoading,
    areUrlApplicationGroupsSuccess,
    areUrlApplicationsError,
    areUrlApplicationsFetching,
    areUrlApplicationsLoading,
    areUrlApplicationsSuccess,
    location.pathname,
    navigate,
    shouldValidateApplications,
    shouldValidateUrlContext,
    urlApplicationGroups,
    urlApplications,
    urlContext.appId,
    urlContext.groupId,
  ]);
};
